import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import { SDialog } from '@modules/ui-components/dialogs/SDialog';
import { FunnelInsightsApiClient } from '../api/insightsClient';
import { EXPERIMENTS_TEST_IDS } from '../experimentsTestIds';
import type {
  WinnerRolloutGate,
  WinnerRolloutPreviewData,
  WinnerRolloutRecord,
  WinnerRolloutVeto,
} from '../types';

/**
 * J2-I1 — one-click Winner Rollout dialog (Experiments hub decided card).
 *
 * Render-only over the server contract: the preview call returns the decision
 * digest plus SERVER-evaluated gate vetoes; this dialog never computes
 * eligibility itself. A 422 veto after confirm re-renders the veto state from
 * the problem+json errors[] verbatim (J2-AC-005 — nothing was mutated).
 * Consequential action → explicit footer Cancel, no header X (design brief).
 */

interface WinnerRolloutDialogProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  experimentId: string;
}

/** The always-visible quality gates; `status` renders as a row only when vetoed. */
const QUALITY_GATES: WinnerRolloutGate[] = ['srm', 'data_quality', 'guardrail', 'sample_floor', 'identity'];

const formatCount = (value: number): string => value.toLocaleString('en-US');

function cleanGateLabel(gate: WinnerRolloutGate, preview: WinnerRolloutPreviewData): string {
  switch (gate) {
    case 'srm':
      return 'Sample ratio (SRM) healthy';
    case 'data_quality':
      return 'Data quality — no flags';
    case 'guardrail':
      return 'Guardrails — none breached';
    case 'sample_floor': {
      const target = preview.decision.minSampleTarget;
      if (target == null) return 'Sample floor — no pre-registered minimum';
      const assigned = preview.decision.scores.map((score) => score.assignedUsers);
      const minAssigned = assigned.length > 0 ? Math.min(...assigned) : 0;
      return `Sample floor reached (${formatCount(minAssigned)} / ${formatCount(target)})`;
    }
    case 'identity':
      return 'Identity — no warnings';
    case 'status':
    default:
      return 'Decision status — rollable';
  }
}

/** Duck-typed extraction of the rollback verification readback (J2-AC-EH2). */
function extractServingState(error: unknown): string | null {
  if (error && typeof error === 'object' && 'servingState' in error) {
    const value = (error as { servingState?: unknown }).servingState;
    if (typeof value === 'string') return value;
  }
  return null;
}

export const WinnerRolloutDialog: React.FC<WinnerRolloutDialogProps> = ({
  open,
  onClose,
  projectId,
  experimentId,
}) => {
  const client = useMemo(() => new FunnelInsightsApiClient(), []);

  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<WinnerRolloutPreviewData | null>(null);
  const [requestError, setRequestError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  /** Set from a 422 after confirm — overrides the preview's veto list. */
  const [confirmVetoes, setConfirmVetoes] = useState<WinnerRolloutVeto[] | null>(null);
  const [result, setResult] = useState<WinnerRolloutRecord | null>(null);
  const [rollingBack, setRollingBack] = useState(false);
  const [rolledBack, setRolledBack] = useState<WinnerRolloutRecord | null>(null);
  const [rollbackError, setRollbackError] = useState<{ message: string; servingState: string | null } | null>(null);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    setPreview(null);
    setRequestError(null);
    setConfirming(false);
    setConfirmVetoes(null);
    setResult(null);
    setRollingBack(false);
    setRolledBack(null);
    setRollbackError(null);
    client.previewWinnerRollout({ projectId, experimentId })
      .then((data) => {
        if (!cancelled) setPreview(data);
      })
      .catch((error: unknown) => {
        if (!cancelled) setRequestError(error instanceof Error ? error.message : 'Rollout preview failed');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, client, projectId, experimentId]);

  const vetoes: WinnerRolloutVeto[] = confirmVetoes ?? preview?.vetoes ?? [];
  const vetoReasonsByGate = useMemo(() => {
    const byGate = new Map<WinnerRolloutGate, string[]>();
    for (const veto of vetoes) {
      byGate.set(veto.gate, [...(byGate.get(veto.gate) ?? []), veto.reason]);
    }
    return byGate;
  }, [vetoes]);

  const winnerName = preview?.winner.variantName ?? preview?.winner.variantId ?? null;
  const variantName = (variantId: string): string =>
    preview?.currentWeights.find((weight) => weight.variantId === variantId)?.name ?? variantId;

  const showResultView = Boolean(result || rolledBack || rollbackError);
  const canRollOut = Boolean(preview?.canRollOut) && confirmVetoes === null;

  const confirmRollout = async () => {
    setConfirming(true);
    setRequestError(null);
    try {
      const outcome = await client.executeWinnerRollout({ projectId, experimentId });
      if (outcome.kind === 'vetoed') {
        // Veto-after-confirm race: the server re-vetoed at execute time —
        // re-render the gate rows from errors[] and keep the primary disabled.
        setConfirmVetoes(outcome.vetoes);
      } else {
        setResult(outcome.rollout);
      }
    } catch (error: unknown) {
      setRequestError(error instanceof Error ? error.message : 'Winner rollout failed');
    } finally {
      setConfirming(false);
    }
  };

  const rollBack = async (rolloutId: string) => {
    setRollingBack(true);
    setRollbackError(null);
    try {
      const restored = await client.rollbackWinnerRollout({ projectId, experimentId, rolloutId });
      setRolledBack(restored);
    } catch (error: unknown) {
      setRollbackError({
        message: error instanceof Error ? error.message : 'Rollout rollback failed',
        servingState: extractServingState(error),
      });
    } finally {
      setRollingBack(false);
    }
  };

  /**
   * alreadyRolledOut info state: the preview carries no rollout id, and the
   * backend contract returns the latest COMPLETED rollout record from execute
   * without mutating anything when the winner already serves 100% — resolve
   * the record that way, then roll it back.
   */
  const rollBackLatest = async () => {
    setRollingBack(true);
    setRollbackError(null);
    try {
      const outcome = await client.executeWinnerRollout({ projectId, experimentId });
      if (outcome.kind === 'vetoed') {
        setConfirmVetoes(outcome.vetoes);
        return;
      }
      const restored = await client.rollbackWinnerRollout({ projectId, experimentId, rolloutId: outcome.rollout.id });
      setRolledBack(restored);
    } catch (error: unknown) {
      setRollbackError({
        message: error instanceof Error ? error.message : 'Rollout rollback failed',
        servingState: extractServingState(error),
      });
    } finally {
      setRollingBack(false);
    }
  };

  const title = showResultView
    ? (rolledBack ? 'Rollout rolled back' : 'Winner rolled out')
    : winnerName
      ? `Roll out winner — ${winnerName}`
      : 'Roll out winner';

  const gateRows: WinnerRolloutGate[] = vetoReasonsByGate.has('status')
    ? ['status', ...QUALITY_GATES]
    : QUALITY_GATES;

  return (
    <SDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      showCloseButton={false}
      data-testid={EXPERIMENTS_TEST_IDS.rolloutDialog}
      title={title}
      actions={result && result.status === 'completed' && !rolledBack
        ? [{
            label: 'Roll back',
            color: 'error' as const,
            variant: 'outlined' as const,
            onClick: () => rollBack(result.id),
            loading: rollingBack,
            'data-testid': EXPERIMENTS_TEST_IDS.rolloutRollback,
          }]
        : undefined}
      secondaryAction={showResultView
        ? undefined
        : {
            label: 'Cancel',
            onClick: onClose,
            disabled: confirming || rollingBack,
            'data-testid': EXPERIMENTS_TEST_IDS.rolloutCancel,
          }}
      primaryAction={showResultView
        ? {
            label: 'Done',
            onClick: onClose,
            disabled: rollingBack,
            'data-testid': EXPERIMENTS_TEST_IDS.rolloutCancel,
          }
        : {
            label: winnerName ? `Roll out ${winnerName}` : 'Roll out',
            onClick: confirmRollout,
            disabled: loading || confirming || rollingBack || !canRollOut,
            loading: confirming,
            'data-testid': EXPERIMENTS_TEST_IDS.rolloutConfirm,
          }}
    >
      <Stack spacing={1.5} sx={{ minWidth: 420, px: 3 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={28} aria-label="Loading rollout preview" />
          </Box>
        )}

        {requestError && <Alert severity="error">{requestError}</Alert>}

        {!showResultView && preview && (
          <>
            {/* Evidence strip — winner + per-variant assigned users + sample floor progress. */}
            <Stack direction="row" spacing={2.5} flexWrap="wrap" useFlexGap>
              <Stack spacing={0.25}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {winnerName ?? '—'}
                </Typography>
                <Typography variant="caption" color="text.secondary">winner</Typography>
              </Stack>
              {preview.decision.scores.map((score) => (
                <Stack key={score.variantId} spacing={0.25}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {formatCount(score.assignedUsers)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {variantName(score.variantId)} — assigned users
                  </Typography>
                </Stack>
              ))}
              {preview.decision.minSampleTarget != null && preview.decision.minSampleProgress != null && (
                <Stack spacing={0.25}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {Math.round(preview.decision.minSampleProgress * 100)}%
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    of {formatCount(preview.decision.minSampleTarget)} / variant
                  </Typography>
                </Stack>
              )}
            </Stack>

            {/* Gate rows — ✓ when clean; vetoed rows carry the exact server reason. */}
            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
              {gateRows.map((gate, index) => {
                const reasons = vetoReasonsByGate.get(gate);
                return (
                  <Stack
                    key={gate}
                    direction="row"
                    spacing={1}
                    alignItems="baseline"
                    sx={{ px: 1.5, py: 0.75, borderTop: index === 0 ? 0 : 1, borderColor: 'divider' }}
                    data-testid={EXPERIMENTS_TEST_IDS.rolloutGate(gate)}
                  >
                    <Typography
                      component="span"
                      variant="body2"
                      sx={{ width: 18, textAlign: 'center', color: reasons ? 'error.main' : 'success.main' }}
                    >
                      {reasons ? '✕' : '✓'}
                    </Typography>
                    <Typography variant="body2" color={reasons ? 'error.main' : 'text.primary'}>
                      {reasons ? reasons.join(' ') : cleanGateLabel(gate, preview)}
                    </Typography>
                  </Stack>
                );
              })}
            </Box>

            {preview.alreadyRolledOut && (
              <Alert
                severity="info"
                action={(
                  <Button
                    color="error"
                    size="small"
                    disabled={rollingBack}
                    onClick={rollBackLatest}
                    data-testid={EXPERIMENTS_TEST_IDS.rolloutRollback}
                  >
                    Roll back
                  </Button>
                )}
              >
                {winnerName ?? 'The winner'} is already serving 100% of traffic — nothing to roll out.
                One click restores the previous variant mix.
              </Alert>
            )}

            {vetoes.length > 0 && (
              <Alert severity="error" data-testid={EXPERIMENTS_TEST_IDS.rolloutVetoBanner}>
                Rollout is blocked server-side. Nothing was changed. Fix the failing gate or
                investigate the experiment before rolling out.
              </Alert>
            )}

            {canRollOut && !preview.alreadyRolledOut && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ borderLeft: 3, borderColor: 'divider', pl: 1.5 }}
              >
                Publishes a new iteration serving {winnerName ?? 'the winner'} at 100% — experiment
                history stays immutable, and you can roll back in one click.
              </Typography>
            )}
          </>
        )}

        {showResultView && (
          <Box data-testid={EXPERIMENTS_TEST_IDS.rolloutResult}>
            {rolledBack ? (
              <Alert severity="success">
                Previous routing restored and verified — the earlier variant mix is serving again.
              </Alert>
            ) : result ? (
              <Alert severity="success">New iteration published.</Alert>
            ) : null}

            {!rolledBack && result?.probe && (
              result.probe.success ? (
                <Typography variant="body2" sx={{ mt: 1 }} data-testid={EXPERIMENTS_TEST_IDS.rolloutProbe}>
                  Runtime probe verified: fresh sessions receive {result.winnerVariantName ?? result.winnerVariantId}.
                </Typography>
              ) : (
                <Alert severity="warning" sx={{ mt: 1 }} data-testid={EXPERIMENTS_TEST_IDS.rolloutProbe}>
                  Runtime probe could not verify the winner
                  {result.probe.unexpectedVariantIds.length > 0
                    ? ` — unexpected variants observed: ${result.probe.unexpectedVariantIds.join(', ')}`
                    : ' — the winner was not observed in fresh sessions'}.
                </Alert>
              )
            )}

            {result && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Audit: new iteration {result.newIterationId ?? '—'} · registry:{' '}
                {(result.registryTransition ?? 'not_found').replace(/_/g, ' ')}
              </Typography>
            )}

            {result?.publish?.webUrl && !rolledBack && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Live URL:{' '}
                <Link href={result.publish.webUrl} target="_blank" rel="noopener noreferrer">
                  {result.publish.webUrl}
                </Link>
              </Typography>
            )}

            {rollbackError && (
              <Alert severity="error" sx={{ mt: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Rollback verification failed — do not assume the previous routing is serving.
                </Typography>
                <Typography variant="body2">{rollbackError.message}</Typography>
                {rollbackError.servingState && (
                  <Typography
                    variant="caption"
                    component="pre"
                    sx={{ mt: 0.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'monospace' }}
                  >
                    Current serving state: {rollbackError.servingState}
                  </Typography>
                )}
              </Alert>
            )}
          </Box>
        )}
      </Stack>
    </SDialog>
  );
};
